import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '../../../lib/cn';
import Button from '../Button';

/**
 * Alert компонент для отображения уведомлений
 */
const Alert = ({
  children,
  variant = 'info',
  size = 'md',
  className = '',
  onClose,
  showIcon = true,
  ...props
}) => {
  const variants = {
    info: cn(
      'bg-blue-50 border-blue-200 text-blue-800',
      'dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
    ),
    success: cn(
      'bg-green-50 border-green-200 text-green-800',
      'dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
    ),
    warning: cn(
      'bg-yellow-50 border-yellow-200 text-yellow-800',
      'dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200'
    ),
    error: cn(
      'bg-red-50 border-red-200 text-red-800',
      'dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
    ),
  };

  const sizes = {
    sm: 'p-3 text-sm',
    md: 'p-4 text-base',
    lg: 'p-5 text-lg',
  };

  const iconMap = {
    info: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h-8m-1 4h-1M5 8a3 3 0 016 3v3a3 3 0 016-3H5a3 3 0 00-3-3V8a3 3 0 00-3-3z" />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4 6 6m6 6-6-6-6-6-6" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h.013" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l7-7m7 7v2a3 3 0 003-3h-1a3 3 0 00-3-3V9a3 3 0 00-3-3h-1a3 3 0 00-3-3z" />
      </svg>
    ),
  };

  return (
    <div 
      className={cn(
        'border rounded-lg',
        variants[variant],
        sizes[size],
        className
      )}
      role="alert"
      {...props}
    >
      <div className="flex">
        {showIcon && (
          <div className="flex-shrink-0 mr-3">
            {iconMap[variant]}
          </div>
        )}
        <div className="flex-1">
          {children}
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="flex-shrink-0 ml-3 p-1 hover:bg-opacity-80"
            aria-label="Закрыть"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        )}
      </div>
    </div>
  );
};

Alert.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  onClose: PropTypes.func,
  showIcon: PropTypes.bool,
};

export default Alert;